"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AnimatedCard } from "@/components/motion/AnimatedCard";
import { Plus } from "@/components/ui/icons";
import { FAQ } from "@/content/quikit";
import { cn } from "@/lib/utils";
import { SectionIntro } from "./SectionIntro";

const EASE = [0.23, 1, 0.32, 1] as const;

type FaqProps = {
  items?: { q: string; a: string }[];
  title?: string;
  text?: string;
  cta?: { label: string; href: string };
  ctaVariant?: "dark" | "light" | "mint";
  id?: string;
};

/** Questions, answered — split layout with an accordion (one open at a time). */
export function Faq({
  items = FAQ,
  title = "Questions, answered",
  text = "Can’t find what you need? Talk to our team.",
  cta = { label: "Contact us", href: "#contact" },
  ctaVariant,
  id = "faq",
}: FaqProps = {}) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id={id} aria-labelledby={`${id}-title`} className="pt-[clamp(120px,12vw,190px)]">
      <div className="wrap grid gap-10 md:grid-cols-[0.8fr_1.2fr] md:gap-14">
        <div className="md:sticky md:top-[calc(var(--nav-h)+48px)] md:self-start">
          <SectionIntro
            align="left"
            size="split"
            eyebrow="FAQ"
            title={title}
            titleId={`${id}-title`}
            text={text}
            cta={cta}
            ctaVariant={ctaVariant}
          />
        </div>
        <ul className="space-y-2">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <AnimatedCard as="li" key={item.q} index={i} stagger={0.06} y={30} scale={1} trigger="self" className="rounded-[20px] bg-panel">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`${id}-${i}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left text-[15px] font-medium tracking-[-0.015em] md:px-6"
                >
                  {item.q}
                  <span
                    className={cn(
                      "grid size-8 shrink-0 place-items-center rounded-full transition-[background-color,color,rotate] duration-300 ease-[var(--ease-premium)]",
                      isOpen ? "rotate-45 bg-ink text-white" : "bg-white text-ink",
                    )}
                  >
                    <Plus className="size-4" />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={`${id}-${i}`}
                      initial={{ height: 0, opacity: 0, filter: "blur(4px)" }}
                      animate={{ height: "auto", opacity: 1, filter: "blur(0px)" }}
                      exit={{ height: 0, opacity: 0, filter: "blur(4px)", transition: { duration: 0.25, ease: EASE } }}
                      transition={{ duration: 0.45, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-[60ch] px-5 pb-5 text-[14px] leading-[1.65] text-muted md:px-6">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </AnimatedCard>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
